// @file Surface ai chat store

import { isAppUsing } from '@@/bits/flip'
import { p__, __ } from '@@/bits/intl'
import { safeLocalStorage } from '@@/bits/safe_storage'
import { SnackbarNotificationType } from '@@/enums'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceOnboardingPanelStore } from '@@/pinia/surface_onboarding_panel'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export enum Role {
  USER = 'user',
  ASSISTANT = 'assistant',
  SYSTEM = 'system',
}

export interface Message {
  role: string
  content: string
}

export interface Suggestion {
  long_message: string
  short_message: string
}

export interface RawMessage {
  object: string
  id: string
  assistant_id: string | null
  attachments: unknown[]
  completed_at: number | null
  content: Array<{
    type: string
    text: {
      annotations: unknown[]
      value: string
    }
  }>
  created_at: number
  incomplete_at: number | null
  incomplete_details: unknown | null
  metadata: Record<string, unknown>
  role: string
  run_id: string | null
  status: string | null
  thread_id: string
}

export enum SuggestionsStatus {
  LOADING = 'loading',
  SUCCESS = 'success',
  ERROR = 'error',
}

const DEFAULT_SUGGESTIONS: Suggestion[] = [
  {
    short_message: __('What can you help me with?'),
    long_message: __('What can you help me with?'),
  },
  {
    short_message: __('Add posts about a topic of my choosing.'),
    long_message: __('Add posts about a topic of my choosing.'),
  },
  {
    short_message: __('How do I add custom fields to my posts?'),
    long_message: __('How do I add custom fields to my posts?'),
  },
]

const MAGIC_TEMPLATE_TO_ASSISTANT_NAME = {
  discussion_board: __('AI recipe assistant'),
  exit_ticket: __('Exit ticket assistant'),
  lesson_plan: __('Lesson plan assistant'),
  class_activities: __('Class activity creator assistant'),
  classroom_activity_ideas: __('Class activities assistant'),
  historical_timeline: __('Timeline of events assistant'),
  reading_list: __('Reading list assistant'),
  map_of_historical_events: __('Historical map assistant'),
  learning_assessment_polls: __('Assessment polls assistant'),
  rubric: __('Rubric assistant'),
  custom_board: __('AI recipe assistant'),
}

export const useSurfaceAIChatStore = defineStore('surfaceAIChatStore', () => {
  const surfaceOnboardingPanelStore = useSurfaceOnboardingPanelStore()
  const surfaceStore = useSurfaceStore()
  const surfacePostsStore = useSurfacePostsStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const xSurfaceAIChatPanel = ref(false)
  const xActionMenu = ref(false)
  const magicWallFirstOpen = ref(false)
  const messages = ref<Message[]>([])
  const threadId = ref<string | null>(null)
  const savedThreadId = ref<string | null>(null)

  const suggestions = ref<Suggestion[]>([])
  const suggestionsStatus = ref<SuggestionsStatus>(SuggestionsStatus.SUCCESS)

  const isLoadingSuggestions = computed(() => suggestionsStatus.value === SuggestionsStatus.LOADING)
  const hasSuggestions = computed(() => suggestions.value.length !== 0)

  const assistantName = computed(() => {
    if (surfaceStore.magicWallType == null) return __('Teaching assistant') // this happens for native admins
    // TODO: remove this once exit ticket is launched and flip has been removed
    // Need to call isAppUsing here to make this assistantName reactive to changes in the flip, because the feature flags are initially undefined and false
    if (surfaceStore.magicWallType === 'exit_ticket' && isAppUsing('exitTicket')) {
      return p__(
        "An Exit ticket is an informal activity at the end of class used to quickly assess students' understanding of lesson objectives. Students must submit their responses or reflections before leaving, like a ticket needed to exit the classroom",
        'Exit ticket assistant',
      )
    }

    return MAGIC_TEMPLATE_TO_ASSISTANT_NAME[surfaceStore.magicWallType] || __('Teaching assistant')
  })

  const hasMessages = computed(() => messages.value.length > 0)
  const isLoadingResponse = ref(false)

  const hasFinishedResponse = computed(() => {
    return !isLoadingResponse.value
  })

  // magicWallFirstOpen is used by SurfaceContainer to control visibility of SurfaceMagicWallFeedbackPopup
  async function loadMagicWallFirstOpen(_showSurfaceChat: boolean): Promise<void> {
    const jsonFormData = safeLocalStorage.getItem('magicWallOptions')
    if (jsonFormData != null) {
      magicWallFirstOpen.value = true
      safeLocalStorage.removeItem('magicWallOptions')
    }
  }

  async function loadThreadMessages(threadId: string, signal?: AbortSignal): Promise<void> {
    try {
      const { SurfaceAIChatApi } = await import('@@/surface/api/surface_ai_chat')
      const response = await SurfaceAIChatApi.getThreadMessages(threadId, surfaceStore.wallHashid, { signal })
      const formattedMessages = response.messages
        .filter((message: RawMessage) => {
          // Only show assistant and user messages, filter out system messages
          if (message.role === 'assistant' || message.role === 'user') {
            const content = message.content[0].text.value
            // Filter out old context messages that shouldn't be displayed
            if (
              content.includes('# user context #') ||
              content.includes('# padlet contents #') ||
              content.includes('# initial instructions #')
            ) {
              return false
            }
            return true
          }
          return false
        })
        .map((message: RawMessage): Message => {
          return {
            role: message.role,
            content: message.content[0].text.value,
          }
        })
        .reverse() // Reverse the array to show oldest messages first

      messages.value = formattedMessages
    } catch (error) {
      messages.value = []
      savedThreadId.value = null
      throw error
    }
  }

  async function initializeAll(): Promise<void> {
    if (messages.value.length > 0 || savedThreadId.value != null) {
      return
    }
    isLoadingResponse.value = true
    try {
      const { SurfaceAIChatApi } = await import('@@/surface/api/surface_ai_chat')
      // Try to get existing thread
      savedThreadId.value = await SurfaceAIChatApi.getThreadId(surfaceStore.wallId, surfaceStore.wallHashid)

      if (savedThreadId.value != null) {
        // Load existing thread messages
        threadId.value = savedThreadId.value
        await loadThreadMessages(savedThreadId.value, AbortSignal.timeout(7000))
      } else {
        // Create new thread to trigger chat creation and initial assistant message generation
        const response = await SurfaceAIChatApi.createThread(surfaceStore.wallHashid)
        threadId.value = response.thread_id
        // Load messages to get the initial assistant message
        if (threadId.value != null) {
          await loadThreadMessages(threadId.value, AbortSignal.timeout(7000))
        }
        await fetchSuggestions()
      }
    } catch (error) {
      if (error.name === 'TimeoutError') {
        // If loading messages takes too long, set threadId to null
        // Don't throw error or it will be handled as a general initialization error
        savedThreadId.value = null
        threadId.value = null
      } else {
        handleInitializationError()
      }
    } finally {
      isLoadingResponse.value = false
    }
  }

  function handleInitializationError(): void {
    xSurfaceAIChatPanel.value = false
    globalSnackbarStore.setSnackbar({
      message: __('The Assistant encountered an error. Please try again later.'),
      notificationType: SnackbarNotificationType.error,
    })
  }

  async function fetchSuggestions(): Promise<void> {
    if (surfacePostsStore.currentPostsCount === 0) {
      suggestions.value = DEFAULT_SUGGESTIONS
      suggestionsStatus.value = SuggestionsStatus.SUCCESS
      return
    }
    try {
      suggestionsStatus.value = SuggestionsStatus.LOADING
      const { SurfaceAIChatApi } = await import('@@/surface/api/surface_ai_chat')
      const response = await SurfaceAIChatApi.getSuggestions(
        surfaceStore.wallHashid,
        { signal: AbortSignal.timeout(5000) }, // This signal will cause getSuggestions to throw a timeout error if the request takes more than 5 seconds
      )
      suggestions.value = response.suggestions
      suggestionsStatus.value = SuggestionsStatus.SUCCESS
    } catch {
      suggestions.value = DEFAULT_SUGGESTIONS
      suggestionsStatus.value = SuggestionsStatus.ERROR
    }
  }

  async function onUserMessage(message: Message): Promise<void> {
    pushUserMessage(message.content)

    pushAssistantMessage('')
    isLoadingResponse.value = true
    const { SurfaceAIChatApi } = await import('@@/surface/api/surface_ai_chat')

    if (threadId.value === null) {
      const response = await SurfaceAIChatApi.createThread(surfaceStore.wallHashid)
      threadId.value = response.thread_id
    }
    // Save thread on user message
    if (savedThreadId.value == null && threadId.value != null) {
      void SurfaceAIChatApi.saveThreadId(surfaceStore.wallId, threadId.value)
      savedThreadId.value = threadId.value
    }
    await SurfaceAIChatApi.sendMessage(threadId.value as string, surfaceStore.wallHashid, message)
  }

  function resetMessages(): void {
    threadId.value = null
    messages.value = []
  }

  function addAssistantMessageDelta(delta: string): void {
    messages.value[messages.value.length - 1].content += delta
  }

  function pushAssistantMessage(content: string): void {
    messages.value.push({
      role: Role.ASSISTANT,
      content,
    })
  }

  function pushUserMessage(content: string): void {
    messages.value.push({
      role: Role.USER,
      content,
    })
  }

  function showChatPanel(): void {
    xSurfaceAIChatPanel.value = true
  }

  function openActionMenu(): void {
    xActionMenu.value = true
  }

  function hidePostActionMenu(): void {
    xActionMenu.value = false
  }

  function clearThread(): void {
    resetMessages()
    void fetchSuggestions()
    savedThreadId.value = null
    void import('@@/surface/api/surface_ai_chat').then(({ SurfaceAIChatApi }) => {
      void SurfaceAIChatApi.deleteThreadForWall(surfaceStore.wallId, surfaceStore.wallHashid)
    })
  }

  async function clearChatMessages(): Promise<void> {
    const { SurfaceAIChatApi } = await import('@@/surface/api/surface_ai_chat')
    const response = await SurfaceAIChatApi.clearChatMessages(surfaceStore.wallHashid)
    if (response?.status === 'ok') {
      messages.value = []
      void fetchSuggestions()
      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.success,
        message: __('Chat messages cleared successfully'),
      })
    } else {
      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.error,
        message: __('Error clearing chat messages'),
      })
    }
  }

  function toggleChatPanel(): void {
    if (surfaceOnboardingPanelStore.showOnboardingPanel) {
      surfaceOnboardingPanelStore.closeOnboardingPanel()
    }
    xSurfaceAIChatPanel.value = !xSurfaceAIChatPanel.value
  }

  async function resetThread(): Promise<void> {
    // Clear messages
    messages.value = []

    // Clear existing thread IDs
    threadId.value = null
    savedThreadId.value = null

    // Reset loading states
    isLoadingResponse.value = false

    const { SurfaceAIChatApi } = await import('@@/surface/api/surface_ai_chat')
    // Delete the thread on the server
    if (surfaceStore.wallId != null) {
      await SurfaceAIChatApi.deleteThreadForWall(surfaceStore.wallId, surfaceStore.wallHashid)
    }

    // Fetch new suggestions
    await fetchSuggestions()

    // Create a new thread
    const response = await SurfaceAIChatApi.createThread(surfaceStore.wallHashid)
    threadId.value = response.thread_id

    // Show snackbar notification
    globalSnackbarStore.setSnackbar({
      message: __('Assistant timed out. Thread reset.'),
      notificationType: SnackbarNotificationType.error,
    })
  }

  return {
    loadThreadMessages,
    xActionMenu,
    suggestions,
    hasSuggestions,
    assistantName,
    messages,
    threadId,
    savedThreadId,
    hasMessages,
    onUserMessage,
    resetMessages,
    showChatPanel,
    openActionMenu,
    hidePostActionMenu,
    toggleChatPanel,
    clearThread,
    clearChatMessages,
    fetchSuggestions,
    xSurfaceAIChatPanel,
    magicWallFirstOpen,
    isLoadingSuggestions,
    isLoadingResponse,
    hasFinishedResponse,
    loadMagicWallFirstOpen,
    initializeAll,
    addAssistantMessageDelta,
    resetThread,
  }
})
